import { Switch } from "@/components/shadcn/switch";
import { Skeleton } from "@/components/shadcn/skeleton";
import {
  Edit,
  ExternalLink,
  Trash,
  TriangleAlert,
  Workflow,
} from "lucide-react";
import { Badge } from "@/components/shadcn/badge";
import { AddNewEntry, Tool } from "./AssistantDetailsContent";
import { useMemo, useState } from "react";
import { NoxusMenu } from "../UI/NoxusMenu";
import {
  InputOverride,
  WorkflowSettingsInput,
  WorkflowWithTriggersAndTags,
} from "@/generated/types.gen";
import { WorkflowService } from "@/generated";
import { AssistantWorkflowPicker } from "./AssistantWorkflowPicker";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useQuery } from "@tanstack/react-query";
import { useSpotUser } from "@/utils/spotUser";
import { NoxusAlert } from "../UI/NoxusAlert";
import { NoxusIcon } from "../UI/NoxusIcon";
import { NoxusTooltip } from "../UI/NoxusTooltip";
import { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";
import { RichTextValue } from "@/components/Form/RichTextField";
import { useDisclosure } from "@/utils/useDisclosure";
import { ExpandableTextarea } from "./ExpandableTextarea";
import { useRouter } from "next/navigation";
import { UnavailableResourceBadge } from "./UnavailableResourceBadge";

export const FlowsField = ({
  tools,
  onToolsChange,
  isNodeConfig,
  isDisabled,
  itemConfig,
  agentVariables,
  externalValuesEnabled,
}: {
  tools: Tool[] | undefined;
  onToolsChange: (tools: Tool[]) => void;
  isNodeConfig?: boolean;
  isDisabled?: boolean;
  itemConfig?: Record<string, NoxusFormEntryConfig>;
  agentVariables?: Array<{ name: string }>;
  externalValuesEnabled?: boolean;
}) => {
  const { spotGroup } = useSpotUser();
  const router = useRouter();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const workflowsTools = useMemo(() => {
    return tools?.filter((tool) => tool.type === "workflow");
  }, [tools]) as WorkflowSettingsInput[] | undefined;

  const workflowIds = useMemo(() => {
    return workflowsTools?.map((t) => t.workflow_id).join(",") || "";
  }, [workflowsTools]);

  const {
    data: workflowsData,
    isLoading: isLoadingWorkflows,
    isSuccess: workflowsLoaded,
  } = useRemoteData(
    DATA_KEYS.WORKFLOWS,
    {
      path: { group_id: spotGroup?.id },
      query: { ids: workflowIds, include_deleted: true },
    },
    {
      staleTime: Infinity,
      enabled: !!workflowIds,
    }
  );

  const { data: availabilityData } = useQuery({
    queryKey: [DATA_KEYS.WORKFLOWS, "availability", workflowIds],
    queryFn: () =>
      WorkflowService.getWorkflows({
        path: { group_id: spotGroup.id },
        query: {
          size: 1,
          page: 1,
          ignore_ids: workflowIds || undefined,
        },
      }),
    staleTime: 30_000,
    enabled: !!spotGroup?.id,
  });
  const remainingWorkflowsCount = availabilityData?.data?.total ?? null;

  const onChange = (tool?: Tool, enabled?: boolean) => {
    if (isDisabled) return;
    if (!tool) {
      return;
    }
    const newTools = tools?.map((t) => {
      if (
        (t as WorkflowSettingsInput).workflow_id ===
        (tool as WorkflowSettingsInput).workflow_id
      ) {
        return { ...t, enabled };
      }
      return t;
    });

    if (newTools) {
      onToolsChange(newTools);
    }
  };

  const onAddWorkflow = (props: {
    workflow_id: string;
    config: Record<string, unknown>;
    inputs_descriptions?: Record<string, string>;
    input_overrides?: Record<string, InputOverride>;
    version_id?: string;
  }) => {
    const {
      workflow_id,
      config,
      inputs_descriptions,
      input_overrides,
      version_id,
    } = props;
    if (isDisabled) return;
    if (editingTool) {
      onToolsChange(
        tools?.map((t) => {
          if (
            (t as WorkflowSettingsInput).workflow_id === editingTool.workflow_id
          ) {
            return {
              ...t,
              ...config,
              inputs_descriptions,
              input_overrides: input_overrides || {},
              version_id: version_id || null,
            };
          }
          return t;
        }) || []
      );
    } else {
      onToolsChange([
        ...(tools || []),
        {
          type: "workflow",
          enabled: true,
          workflow_id,
          ...config,
          inputs_descriptions,
          input_overrides: input_overrides || {},
          version_id: version_id || null,
        } as WorkflowSettingsInput,
      ]);
    }
  };

  const activeWorkflows = useMemo(() => {
    return (
      workflowsTools?.map((tool) => ({
        id: tool.workflow_id,
        name: tool.workflow_id,
      })) || []
    );
  }, [workflowsTools]);

  const [editingTool, setEditingTool] = useState<
    WorkflowSettingsInput | undefined
  >(undefined);

  const onEdit = (tool: WorkflowSettingsInput) => {
    if (isDisabled) return;
    setEditingTool(tool);
    onOpen();
  };

  const onDelete = (tool: WorkflowSettingsInput) => {
    if (isDisabled) return;
    if (tools) {
      onToolsChange(
        tools.filter(
          (t) => (t as WorkflowSettingsInput).workflow_id !== tool.workflow_id
        )
      );
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div>
          <p className="text-sm font-medium text-foreground leading-none">
            Flows
          </p>
          <p className="text-xs font-regular text-muted-foreground mt-1.5">
            Select the flows this agent can access.
          </p>
        </div>

        {workflowsTools?.map((tool) => {
          const workflowData = workflowsData?.items?.find(
            (w) => w.id === tool.workflow_id
          );
          const isMissing =
            workflowsLoaded && (!workflowData || !!workflowData.deleted_at);
          return (
            <WorkflowEntry
              key={tool.workflow_id}
              tool={tool}
              workflowData={workflowData}
              isMissing={isMissing}
              isLoadingData={isLoadingWorkflows}
              onChange={(enabled) => {
                onChange(tool, enabled);
              }}
              onDelete={() => {
                onDelete(tool);
              }}
              onEdit={() => {
                onEdit(tool);
              }}
              onExtraInstructionsChange={(value) => {
                if (isDisabled) return;
                const newTools = tools?.map((t) => {
                  if (
                    (t as WorkflowSettingsInput).workflow_id ===
                    tool.workflow_id
                  ) {
                    return { ...t, extra_instructions: value };
                  }
                  return t;
                });
                if (newTools) {
                  onToolsChange(newTools as Tool[]);
                }
              }}
              isDisabled={isDisabled}
              fieldConfig={itemConfig?.["extra_instructions"]}
            />
          );
        })}

        {(workflowsTools?.length ?? 0) === 0 &&
        remainingWorkflowsCount === 0 ? (
          <NoxusAlert
            icon={Workflow}
            description="No flows available."
            action={{
              label: (
                <>
                  <ExternalLink />
                  Go to Workshop
                </>
              ),
              onClick: () => router.push("/?tab=flows"),
            }}
          />
        ) : (
          <AddNewEntry
            label="Add Flow"
            disabledLabel={
              remainingWorkflowsCount === 0 ? "No more flows to add" : undefined
            }
            onClick={onOpen}
            isDisabled={isDisabled || remainingWorkflowsCount === 0}
          />
        )}
      </div>
      {isOpen && (
        <AssistantWorkflowPicker
          isOpen={isOpen}
          onClose={() => {
            setEditingTool(undefined);
            onClose();
          }}
          onSelectWorkflow={onAddWorkflow}
          openWorkflows={activeWorkflows}
          editingTool={editingTool}
          itemConfig={itemConfig}
          agentVariables={agentVariables}
          externalValuesEnabled={externalValuesEnabled}
        />
      )}
    </div>
  );
};
const WorkflowEntry = ({
  tool,
  workflowData,
  isMissing,
  isLoadingData,
  onChange,
  onDelete,
  onEdit,
  onExtraInstructionsChange,
  isDisabled,
  fieldConfig,
}: {
  tool: WorkflowSettingsInput;
  workflowData?: WorkflowWithTriggersAndTags;
  isMissing?: boolean;
  isLoadingData?: boolean;
  onChange: (enabled: boolean) => void;
  onDelete: () => void;
  onEdit: () => void;
  onExtraInstructionsChange: (value: RichTextValue) => void;
  isDisabled?: boolean;
  fieldConfig?: NoxusFormEntryConfig;
}): React.JSX.Element => {
  const { name } = workflowData || {};
  const { spotGroup } = useSpotUser();
  const router = useRouter();

  const { data: validationResult } = useQuery({
    queryKey: [
      DATA_KEYS.WORKFLOWS_ID,
      "valid",
      tool.workflow_id,
      tool.version_id,
    ],
    queryFn: () =>
      WorkflowService.getIsValidWorkflow({
        path: {
          group_id: spotGroup.id,
          workflow_id: tool.workflow_id,
        },
        query: {
          version_id: tool.version_id || undefined,
        },
      }),
    enabled: !!workflowData && !isMissing,
    staleTime: 60_000,
  });

  const isInvalid = validationResult?.data?.is_valid === false;
  const errorMessages = useMemo(() => {
    if (!validationResult?.data?.exceptions) return [];
    const msgs: string[] = [];
    for (const errors of Object.values(validationResult.data.exceptions)) {
      for (const err of errors) {
        const snippet = err.snippet as string | undefined;
        const detail = err.detail as string | undefined;
        msgs.push(snippet || detail || "Unknown validation error");
      }
    }
    return msgs;
  }, [validationResult]);

  return (
    <div className="flex flex-col border border-border rounded-lg w-full overflow-hidden">
      <div className="flex items-center justify-between gap-2 p-2">
        <div className="flex items-center gap-2 flex-1">
          <NoxusIcon icon={workflowData?.meta?.icon || "workflow"} />
          <div className="flex-1 flex items-center gap-2">
            {isLoadingData ? (
              <Skeleton className="h-5 w-32 mb-1" />
            ) : (
              <>
                <NoxusTooltip content="Open in new tab" placement="top" asChild>
                  <a
                    href={`/flows/${tool.workflow_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    <span className="text-sm">{name || "Deleted flow"}</span>
                  </a>
                </NoxusTooltip>
                {isMissing && <UnavailableResourceBadge kind="flow" />}
              </>
            )}
            {isInvalid && !isMissing && (
              <NoxusTooltip
                content={errorMessages.join("; ") || "Workflow is invalid"}
                placement="top"
                asChild
              >
                <Badge variant="warning" className="gap-1">
                  <TriangleAlert />
                  Invalid
                </Badge>
              </NoxusTooltip>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={tool.enabled}
            onCheckedChange={(checked) => onChange(checked)}
            disabled={isDisabled}
          />
          <NoxusMenu
            isSmall
            itemsArray={[
              {
                label: "Edit",
                icon: Edit,
                onClick: onEdit,
                isHidden: isMissing,
              },
              {
                label: "Open in new tab",
                icon: ExternalLink,
                onClick: () => {
                  router.push(`/flows/${tool.workflow_id}`);
                },
              },
              {
                label: "Remove",
                icon: Trash,
                onClick: onDelete,
                color: "destructive",
              },
            ]}
            isDisabled={isDisabled}
          />
        </div>
      </div>

      {tool.enabled && !isMissing && !isInvalid && (
        <ExpandableTextarea
          value={tool.extra_instructions || ""}
          onChange={(value) => onExtraInstructionsChange(value)}
          isDisabled={isDisabled}
          label="Flow usage conditions"
          placeholder="Specify when and how this flow should be used..."
          fieldConfig={fieldConfig}
        />
      )}
    </div>
  );
};
